'use client'

import React, { useMemo } from 'react'
import styles from './feedback.module.css'

/** Explicit connection lifecycle — prefer over boolean `connected`. */
export type ConnectionStatusState =
  | 'connected'
  | 'connecting'
  | 'error'
  | 'disconnected'

export interface ConnectionStatusProps {
  /**
   * Preferred multi-state API.
   * When omitted, falls back to `connected` boolean for backward compatibility.
   */
  status?: ConnectionStatusState
  /**
   * Legacy boolean API. `true` → connected, `false` → disconnected.
   * Ignored when `status` is set.
   */
  connected?: boolean
  /** Optional label override (still paired with tone + dot). */
  label?: string
  className?: string
}

const STATE_META: Record<
  ConnectionStatusState,
  { label: string; tone: string; aria: string }
> = {
  connected: {
    label: 'Connected',
    tone: styles.connectionOk ?? '',
    aria: 'Connection status: Connected',
  },
  connecting: {
    label: 'Connecting…',
    tone: styles.connectionConnecting ?? '',
    aria: 'Connection status: Connecting',
  },
  error: {
    label: 'Connection error',
    tone: styles.connectionError ?? '',
    aria: 'Connection status: Error',
  },
  disconnected: {
    label: 'Disconnected',
    tone: styles.connectionDown ?? '',
    aria: 'Connection status: Disconnected',
  },
}

/**
 * Compact connection indicator — text + status dot (never color alone).
 *
 * States: connected | connecting | error | disconnected
 * Announces changes via aria-live="polite". Smooth 200ms tone transitions.
 */
export function ConnectionStatus({
  status,
  connected = true,
  label: labelOverride,
  className,
}: ConnectionStatusProps) {
  const resolved = useMemo<ConnectionStatusState>(() => {
    if (status) return status
    return connected ? 'connected' : 'disconnected'
  }, [status, connected])

  const meta = STATE_META[resolved]
  const label = labelOverride ?? meta.label

  return (
    <div
      className={`${styles.connection} ${meta.tone}${className ? ` ${className}` : ''}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={labelOverride ? `Connection status: ${label}` : meta.aria}
      data-status={resolved}
      title={labelOverride ? `Connection status: ${label}` : meta.aria}
    >
      <span className={styles.connectionDot} aria-hidden="true" />
      <span className={styles.connectionLabel}>{label}</span>
    </div>
  )
}

export default ConnectionStatus
